import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRef } from "react";
import Menu from "@mui/material/Menu";
import { Tooltip, MenuItem } from "@mui/material";

/**
 * DEV - IBL
 * Author: Ferro Zapata Arnulfo
 * Full Stack Developer
 *
 * SubMenu component renders a sidebar menu item with optional submenu.
 *
 * @param {boolean} isMenuOpen - Determines if the sidebar is in expanded mode.
 * @param {string} changeStyle - Current style identifier for active menu item.
 * @param {number|string} isHovering - Current hover state identifier.
 * @param {number|string} numHovering - Hover identifier for this menu item.
 * @param {string} url - Navigation URL for the menu item.
 * @param {string} label - Translation key for the menu label.
 * @param {React.Component} Icon - Icon component to render.
 * @param {Function} onHover - Handler for mouse enter event.
 * @param {Function} onLeave - Handler for mouse leave event.
 * @param {Function} onClick - Click handler for the menu item.
 * @param {number} index - Index of the menu item.
 * @param {string} openMenu - Identifier of the currently open submenu.
 * @param {Function} handleMenuToggle - Function to toggle submenu open/close.
 * @param {string} menuId - Unique identifier for the submenu.
 * @param {string} name - Name of the menu item.
 * @param {React.ReactNode} children - Submenu items (if any).
 * @param {boolean} onlyName - If true, renders a simple menu item without submenu.
 * @returns {JSX.Element}
 */
const SubMenu = ({
                     isMenuOpen,
                     changeStyle,
                     isHovering,
                     numHovering,
                     url,
                     label,
                     Icon,
                     onHover,
                     onLeave,
                     onClick,
                     index,
                     openMenu,
                     handleMenuToggle,
                     menuId,
                     name,
                     children,
                     onlyName,
                 }) => {
    const menuButtonRef = useRef(null);
    const isOpen = openMenu === menuId;
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleClick = () => {
        if (onClick) {
            onClick();
        }
        if (url) {
            navigate(url);
        }
    };

    // Render the common menu content wrapped with Tooltip.
    const renderMenuContent = () => (
        <div id={`sbitm-menu-${name}${index}`}
             className={`w-100 minw-100 maxw-100 h-30px
             ${isMenuOpen ? "": "d-flex justify-content-center align-items-center" }`}>
            <div
                className={
                    changeStyle === name
                        ? "activeLi"
                        : isHovering === numHovering
                            ? ""
                            : ""
                }
                onMouseEnter={onHover}
                onMouseLeave={onLeave}
            >
                <div className={`infoMenu ${isMenuOpen ? "infoMOpen" : "infoMClose"}`}>
                    <div className={"d-flex align-items-center justify-content-center w-h-24px"}>
                        <Icon className={"btn-icon-svg-24px"} />
                    </div>
                    <span
                        className={`profile-span text-left ${
                            isMenuOpen ? "" : " hiddenText"
                        }`}
                    >
            {t(label)}
          </span>
                </div>
            </div>
        </div>
    );

    if (onlyName) {
        return (
            <MenuItem
                id={`nboh_btn_menu-item-sidebar-${index}`}
                key={index}
                onClick={handleClick}
                className="side-menu-item justify-content-flex-start p-0 m-0"
            >
                <Tooltip
                    title={!isMenuOpen ? t(label)?.toUpperCase() : ""}
                    placement="right"
                    disableInteractive
                >
                    {renderMenuContent()}
                </Tooltip>
            </MenuItem>
        );
    }

    return (
        <>
            <MenuItem
                key={index}
                id={`nboh_btn_menu-item-sidebar-${name}${index}`}
                onClick={(e) => {
                    e.stopPropagation();
                    handleMenuToggle(menuId);
                }}
                className={`p-0 m-0 side-menu-item justify-content-flex-start ${
                    isOpen ? "menu-open" : ""
                }`}
                ref={menuButtonRef}
            >
                <Tooltip
                    title={!isMenuOpen ? t(label)?.toUpperCase() : ""}
                    placement="right"
                    disableInteractive
                >
                    {renderMenuContent()}
                </Tooltip>
            </MenuItem>
            <Menu
                id="smo-menu-sidebar"
                className="p-0 custom-paper"
                anchorEl={menuButtonRef.current}
                open={isOpen}
                onClose={() => handleMenuToggle(menuId)}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                autoFocus
                variant="selectedMenu"
            >
                {children}
            </Menu>
        </>
    );
};

export default SubMenu;
